import { useEffect, useState } from 'react';
import axios from 'api/axiosInstance';
import dayjs from 'dayjs';

// material-ui
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';

// icons
import { EyeOutlined, SearchOutlined, DeleteOutlined, ExclamationCircleOutlined, TeamOutlined, TableOutlined, PhoneOutlined } from '@ant-design/icons';

// project imports
import MainCard from 'components/MainCard';
import endpoints from 'api/endpoints';
import AppointmentDetailsModal from 'components/AppointmentDetailsModal';

// ==============================|| USER LIST ||============================== //

const headCells = [
  { id: 'hospital_id', align: 'left', label: 'Patient ID' },
  { id: 'first_name', align: 'left', label: 'First Name' },
  { id: 'last_name', align: 'left', label: 'Last Name' },
  { id: 'email', align: 'left', label: 'Email' },
  { id: 'phone', align: 'left', label: 'Phone' },
  { id: 'booking_count', align: 'center', label: 'Bookings' },
  { id: 'last_booking', align: 'left', label: 'Last Booking' },
  { id: 'actions', align: 'center', label: 'Actions' }
];

function UserTableHead({ allChecked, someChecked, onToggleAll }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" sx={{ width: 44 }}>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onChange={onToggleAll}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.align} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Hospital tenants refer to their end-users as "Patients".
const isHospitalTenant = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    const name = (u.industry_name || '').toLowerCase();
    const code = (u.industry_code || '').toUpperCase();
    return name.includes('hospital') || name.includes('clinic') || code.startsWith('HOS');
  } catch {
    return false;
  }
};

function UserList() {
  const pageTitle = isHospitalTenant() ? 'Patients' : 'User List';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  // Full dataset (all pages) used by the grouped views so grouping spans every
  // page, not just the current one. Only fetched while a grouped view is active.
  const [allUsers, setAllUsers] = useState(null);
  const [allLoading, setAllLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState('');
  // View mode: 'table' | 'grouped' (phone) | 'grouped-email' | 'grouped-id' | 'grouped-name'
  const [viewMode, setViewMode] = useState('table');
  const [tab, setTab] = useState('all');

  // Merge
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState('');

  const fetchEndUsers = async (url = null, searchQuery = '') => {
    const companyId = localStorage.getItem('tenant_id');
    const token = localStorage.getItem('token');

    if (!companyId) {
      setError('Company ID not found');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      let requestUrl = url || endpoints.endUsers.list(companyId);
      if (!url && searchQuery) {
        const sep = requestUrl.includes('?') ? '&' : '?';
        requestUrl = `${requestUrl}${sep}search=${encodeURIComponent(searchQuery)}`;
      }
      const response = await axios.get(requestUrl, {
        headers: {
              Authorization: `Bearer ${token}`,
        }
      });
      setUsers(response.data);
      setNextUrl(response.data.next);
      setPreviousUrl(response.data.previous);
    } catch (err) {
      console.error('Failed to fetch end users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch every page (cursor pagination) so grouped views can group across the
  // whole tenant, not just the loaded page. Bounded for safety.
  const fetchAllEndUsers = async (searchQuery = '') => {
    const companyId = localStorage.getItem('tenant_id');
    const token = localStorage.getItem('token');
    if (!companyId) return;
    setAllLoading(true);
    try {
      let url = endpoints.endUsers.list(companyId);
      const sep = url.includes('?') ? '&' : '?';
      url = `${url}${sep}page_size=100${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`;
      const acc = [];
      let guard = 0;
      while (url && guard < 500) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        acc.push(...(resp.data?.results || []));
        url = resp.data?.next || null;
        guard += 1;
      }
      setAllUsers(acc);
    } catch (err) {
      console.error('Failed to fetch all users for grouping:', err);
      setAllUsers([]);
    } finally {
      setAllLoading(false);
    }
  };

  const handleNextPage = () => {
    if (nextUrl) {
      fetchEndUsers(nextUrl);
    }
  };

  const handlePreviousPage = () => {
    if (previousUrl) {
      fetchEndUsers(previousUrl);
    }
  };

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      fetchEndUsers(null, search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // When a grouped view is active, load the full dataset (all pages) so grouping
  // spans everything. Re-runs on search too so grouped results stay in sync.
  useEffect(() => {
    if (viewMode === 'table') return undefined;
    const t = setTimeout(() => { fetchAllEndUsers(search.trim()); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, search]);

  const handleViewUser = async (userId) => {
    const token = localStorage.getItem('token');

    try {
      const response = await axios.get(endpoints.endUsers.getUser(userId), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSelectedUser(response.data);
      setModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  // Bulk select / delete
  const pageUsers = users?.results || [];
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    const pageIds = pageUsers.map((u) => u.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const token = localStorage.getItem('token');
    setBulkDeleting(true);
    setBulkError('');
    try {
      await axios.post(
        endpoints.endUsers.bulkDelete,
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      clearSelection();
      setDeleteConfirmOpen(false);
      fetchEndUsers(null, search.trim());
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Failed to delete selected users');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Reset selection when search changes
  useEffect(() => { clearSelection(); }, [search]);

  // Merge handler
  const openMerge = (source, target) => {
    setMergeSource(source); setMergeTarget(target); setMergeError(''); setMergeConfirmOpen(true);
  };
  // Per-row "Merge" menu (choose which duplicate to merge into) — replaces the
  // old wall of per-target buttons that overflowed the card.
  const [mergeMenu, setMergeMenu] = useState({ anchor: null, user: null });
  const fullName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `#${u.id}`;
  const initials = (u) => (`${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.trim() || (u.email || '?')[0] || '?').toUpperCase();
  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    setMerging(true); setMergeError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(endpoints.endUsers.merge, { source_id: mergeSource.id, target_id: mergeTarget.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMergeConfirmOpen(false); setMergeSource(null); setMergeTarget(null);
      fetchEndUsers(null, search.trim());
    } catch (err) {
      setMergeError(err.response?.data?.error || 'Merge failed');
    } finally { setMerging(false); }
  };

  const pageIds = pageUsers.map((u) => u.id);
  const allChecked = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someChecked = pageIds.some((id) => selectedIds.has(id));

  // Grouped views operate on the full dataset (all pages); table view on the page.
  const groupUsers = viewMode !== 'table' ? (allUsers || []) : pageUsers;

  // Live group-by-phone: map phone → array of users
  const phoneGroups = (() => {
    const map = new Map();
    groupUsers.forEach((u) => {
      const key = (u.mobile || '').trim();
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    return Array.from(map.entries())
      .map(([phone, users]) => ({ key: phone, label: phone, users, icon: 'phone' }))
      .sort((a, b) => b.users.length - a.users.length);
  })();
  const noPhoneUsers = groupUsers.filter((u) => !(u.mobile || '').trim());

  // Live group-by-email: map email → array of users
  const emailGroups = (() => {
    const map = new Map();
    groupUsers.forEach((u) => {
      const key = (u.email || '').trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    return Array.from(map.entries())
      .map(([email, users]) => ({ key: email, label: email, users, icon: 'email' }))
      .sort((a, b) => b.users.length - a.users.length);
  })();
  const noEmailUsers = groupUsers.filter((u) => !(u.email || '').trim());

  // Group by hospital_id
  const idGroups = (() => {
    const map = new Map();
    groupUsers.forEach((u) => {
      const key = (u.hospital_id || '').trim();
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    return Array.from(map.entries())
      .map(([id, users]) => ({ key: id, label: id, users }))
      .sort((a, b) => b.users.length - a.users.length);
  })();
  const noIdUsers = groupUsers.filter((u) => !(u.hospital_id || '').trim());

  // Group by name
  const nameGroups = (() => {
    const map = new Map();
    groupUsers.forEach((u) => {
      const key = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    return Array.from(map.entries())
      .map(([name, users]) => ({ key: name, label: users[0] ? `${users[0].first_name || ''} ${users[0].last_name || ''}`.trim() : name, users }))
      .sort((a, b) => b.users.length - a.users.length);
  })();
  const noNameUsers = groupUsers.filter((u) => !`${u.first_name || ''} ${u.last_name || ''}`.trim());

  // Pick active groups based on viewMode
  const groupConfig = {
    'grouped': { groups: phoneGroups, noKey: noPhoneUsers, label: 'phone' },
    'grouped-email': { groups: emailGroups, noKey: noEmailUsers, label: 'email' },
    'grouped-id': { groups: idGroups, noKey: noIdUsers, label: 'patient ID' },
    'grouped-name': { groups: nameGroups, noKey: noNameUsers, label: 'name' },
  };
  const isGroupedView = viewMode in groupConfig;
  const { groups: activeGroups, noKey: activeNoKeyUsers, label: groupKeyLabel } = groupConfig[viewMode] || { groups: [], noKey: [], label: '' };
  const visibleGroups = tab === 'grouped-only'
    ? activeGroups.filter((g) => g.users.length > 1)
    : activeGroups;

  return (
    <Grid container rowSpacing={4.5} columnSpacing={2.75}>
      {/* Page Header */}
      <Grid sx={{ mb: -2.25 }} size={12}>
        <Typography variant="h5">{pageTitle}</Typography>
      </Grid>

      {/* Search + Bulk actions */}
      <Grid size={12}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by patient ID, name, email, or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined style={{ fontSize: 16, color: '#94A3B8' }} />
                </InputAdornment>
              )
            }}
            sx={{ width: 420 }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) { setViewMode(v); setTab('all'); } }}
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.78rem' }}>
              <TableOutlined style={{ marginRight: 5 }} /> Table
            </ToggleButton>
            <ToggleButton value="grouped-id" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.78rem' }}>
              ID
            </ToggleButton>
            <ToggleButton value="grouped-name" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.78rem' }}>
              Name
            </ToggleButton>
            <ToggleButton value="grouped-email" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.78rem' }}>
              Email
            </ToggleButton>
            <ToggleButton value="grouped" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.78rem' }}>
              Phone
            </ToggleButton>
          </ToggleButtonGroup>
          {selectedIds.size > 0 && (
            <Card sx={{
              px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2,
              bgcolor: '#F0FDFA', border: '1px solid #CCFBF1', boxShadow: 'none', borderRadius: 2,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D9488' }}>
                {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'} selected
              </Typography>
              <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none', color: '#64748B' }}>
                Clear
              </Button>
              <Button
                size="small" variant="contained" startIcon={<DeleteOutlined />}
                onClick={() => { setBulkError(''); setDeleteConfirmOpen(true); }}
                sx={{ textTransform: 'none', bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' }, boxShadow: 'none' }}
              >
                Delete Selected
              </Button>
            </Card>
          )}
        </Stack>
      </Grid>

      {/* User Table */}
      <Grid size={12}>
        <MainCard content={false}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : isGroupedView ? (
            <Box>
              {/* Tabs: All vs Grouped-only (2+ users) */}
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  px: 2, minHeight: 42,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.82rem', minHeight: 42 },
                  '& .Mui-selected': { color: '#0D9488 !important', fontWeight: 700 },
                  '& .MuiTabs-indicator': { bgcolor: '#0D9488', height: 2 }
                }}
              >
                <Tab label={`All ${groupKeyLabel}s (${activeGroups.length})`} value="all" />
                <Tab label={`Grouped (${activeGroups.filter((g) => g.users.length > 1).length})`} value="grouped-only" />
              </Tabs>
              <Divider />
              <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, alignItems: 'start' }}>
                {allLoading && visibleGroups.length === 0 ? (
                  <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary">Loading all patients…</Typography>
                  </Box>
                ) : visibleGroups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
                    {tab === 'grouped-only'
                      ? `No ${groupKeyLabel}s are shared across multiple patients.`
                      : `No patients with ${groupKeyLabel}s.`}
                  </Typography>
                ) : (
                  visibleGroups.map((group) => (
                    <Card
                      key={group.key}
                      sx={{
                        border: '1px solid',
                        borderColor: group.users.length > 1 ? '#CCFBF1' : 'grey.100',
                        boxShadow: 'none', borderRadius: 2, overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, bgcolor: group.users.length > 1 ? '#F0FDFA' : '#F8FAFC', borderBottom: '1px solid', borderColor: group.users.length > 1 ? '#CCFBF1' : 'grey.100' }}>
                        <PhoneOutlined style={{ transform: 'scaleX(-1)', color: group.users.length > 1 ? '#0D9488' : '#64748B', fontSize: 14 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {group.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${group.users.length} ${group.users.length === 1 ? 'user' : 'users'}`}
                          sx={{ fontWeight: 600, height: 22, bgcolor: group.users.length > 1 ? '#0D9488' : '#E2E8F0', color: group.users.length > 1 ? '#fff' : '#475569' }}
                        />
                      </Box>
                      <Stack divider={<Divider flexItem />}>
                        {group.users.map((u) => {
                          const isSel = selectedIds.has(u.id);
                          return (
                            <Box
                              key={u.id}
                              sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: isSel ? '#F0FDFA' : 'inherit' }}
                            >
                              <Checkbox size="small" checked={isSel} onChange={() => toggleSelect(u.id)} sx={{ p: 0.5, flexShrink: 0 }} />
                              <Avatar sx={{ width: 34, height: 34, flexShrink: 0, fontSize: '0.8rem', fontWeight: 700, bgcolor: '#E0F2F1', color: '#0D9488' }}>
                                {initials(u)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {`${u.first_name || ''} ${u.last_name || ''}`.trim() || '(no name)'}
                                  </Typography>
                                  {u.hospital_id && (
                                    <Chip size="small" label={u.hospital_id} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#F0FDFA', color: '#0D9488', flexShrink: 0 }} />
                                  )}
                                  {u.is_draft && (
                                    <Chip size="small" label="Draft" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE', flexShrink: 0 }} />
                                  )}
                                </Stack>
                                <Typography variant="caption" sx={{ color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                  {u.email || 'no email'} · {u.booking_count ?? 0} booking{(u.booking_count ?? 0) === 1 ? '' : 's'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                <Tooltip title="View history">
                                  <IconButton size="small" color="primary" onClick={() => handleViewUser(u.id)}>
                                    <EyeOutlined />
                                  </IconButton>
                                </Tooltip>
                                {group.users.length > 1 && (
                                  <Button size="small" onClick={(e) => setMergeMenu({ anchor: e.currentTarget, user: u, others: group.users.filter((o) => o.id !== u.id) })}
                                    sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1.25, py: 0.25, borderRadius: 1.5, color: '#7C3AED', bgcolor: '#F5F3FF', border: '1px solid #DDD6FE', minWidth: 0, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#EDE9FE' } }}>
                                    Merge…
                                  </Button>
                                )}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Card>
                  ))
                )}
              </Box>
              {activeNoKeyUsers.length > 0 && tab === 'all' && (
                <>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {activeNoKeyUsers.length} patient{activeNoKeyUsers.length === 1 ? '' : 's'} without a {groupKeyLabel}
                    </Typography>
                  </Box>
                </>
              )}
              {/* Pagination */}
              <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
                <Button variant="outlined" disabled={!previousUrl} onClick={handlePreviousPage}>Previous</Button>
                <Button variant="outlined" disabled={!nextUrl} onClick={handleNextPage}>Next</Button>
              </Stack>
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  position: 'relative',
                  display: 'block',
                  maxWidth: '100%',
                  '& td, & th': { whiteSpace: 'nowrap' }
                }}
              >
                <Table aria-labelledby="tableTitle">
                  <UserTableHead allChecked={allChecked} someChecked={someChecked} onToggleAll={toggleSelectAllOnPage} />
                  <TableBody>
                    {!users?.results || users.results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={headCells.length + 1} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.results.map((user, index) => (
                        <TableRow
                          hover
                          role="checkbox"
                          sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: selectedIds.has(user.id) ? '#F0FDFA' : 'inherit' }}
                          tabIndex={-1}
                          key={user.id || index}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedIds.has(user.id)}
                              onChange={() => toggleSelect(user.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {user.hospital_id || 'N/A'}
                              {user.is_draft && (
                                <Chip size="small" label="Draft" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{user.first_name || 'N/A'}</TableCell>
                          <TableCell>{user.last_name || 'N/A'}</TableCell>
                          <TableCell>{user.email || 'N/A'}</TableCell>
                          <TableCell>{user.mobile || 'N/A'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={user.booking_count ?? 0}
                              size="small"
                              color={user.booking_count > 0 ? 'primary' : 'default'}
                              sx={{ fontWeight: 600, minWidth: 36 }}
                            />
                          </TableCell>
                          <TableCell>
                            {user.last_booking
                              ? dayjs(user.last_booking).format('MMM DD, YYYY')
                              : '—'}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View History">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleViewUser(user.id)}
                              >
                                <EyeOutlined />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
                <Button
                  variant="outlined"
                  disabled={!previousUrl}
                  onClick={handlePreviousPage}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  disabled={!nextUrl}
                  onClick={handleNextPage}
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </MainCard>
      </Grid>

      {/* User Details Modal */}
      <AppointmentDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        data={selectedUser}
      />

      {/* Bulk Delete Confirm */}
      <Modal open={deleteConfirmOpen} onClose={() => !bulkDeleting && setDeleteConfirmOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 440 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExclamationCircleOutlined style={{ color: '#B91C1C', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Delete Users?</Typography>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, mb: 1.5 }}>
              You are about to permanently delete <strong>{selectedIds.size}</strong> patient{selectedIds.size === 1 ? '' : 's'}.
            </Typography>
            <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>
              This will also delete <strong>all appointments, testimonials, and history</strong> associated with {selectedIds.size === 1 ? 'this patient' : 'these patients'}. This action cannot be undone.
            </Alert>
            {bulkError && <Alert severity="error" sx={{ mt: 1.5 }}>{bulkError}</Alert>}
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} disabled={bulkDeleting}
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleBulkDelete} disabled={bulkDeleting}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' }, boxShadow: 'none' }}>
              {bulkDeleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Delete ${selectedIds.size}`}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Per-row Merge menu: choose which duplicate to merge the record into */}
      <Menu
        anchorEl={mergeMenu.anchor}
        open={Boolean(mergeMenu.anchor)}
        onClose={() => setMergeMenu({ anchor: null, user: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: '#94A3B8', display: 'block' }}>
          Merge {mergeMenu.user ? fullName(mergeMenu.user) : ''} into…
        </Typography>
        {(mergeMenu.others || []).map((other) => (
          <MenuItem key={other.id} onClick={() => { openMerge(mergeMenu.user, other); setMergeMenu({ anchor: null, user: null }); }}>
            <Avatar sx={{ width: 26, height: 26, mr: 1.25, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#EDE9FE', color: '#7C3AED' }}>
              {initials(other)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullName(other)}</Typography>
              <Typography variant="caption" color="text.secondary">{other.email || other.hospital_id || `#${other.id}`}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Merge Confirm */}
      <Modal open={mergeConfirmOpen} onClose={() => !merging && setMergeConfirmOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 440 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TeamOutlined style={{ color: '#7C3AED', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Merge Patients?</Typography>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            {mergeSource && mergeTarget && (
              <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, mb: 1.5 }}>
                All appointments from <strong>{`${mergeSource.first_name || ''} ${mergeSource.last_name || ''}`.trim()}</strong>
                {mergeSource.hospital_id && <> ({mergeSource.hospital_id})</>} will be moved to{' '}
                <strong>{`${mergeTarget.first_name || ''} ${mergeTarget.last_name || ''}`.trim()}</strong>
                {mergeTarget.hospital_id && <> ({mergeTarget.hospital_id})</>}.
                The source patient will be deleted.
              </Typography>
            )}
            <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>This action cannot be undone.</Alert>
            {mergeError && <Alert severity="error" sx={{ mt: 1.5 }}>{mergeError}</Alert>}
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button variant="outlined" onClick={() => setMergeConfirmOpen(false)} disabled={merging}
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" onClick={handleMerge} disabled={merging}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' }, boxShadow: 'none' }}>
              {merging ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Merge'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
}

export default UserList;
